import {Chart} from "@highcharts/react";
import Highcharts from "highcharts/highcharts";
import "highcharts/highcharts-more";
import "highcharts/modules/exporting";
import "highcharts/modules/accessibility";
import {useState, useEffect, useMemo, memo, Component} from "react";
import type {ErrorInfo, ReactNode} from "react";
import {merge} from "lodash";
import dataUtils from "@/util/dataFormatUtil";
import {
    IColumnMetadata,
    IDataTableData
} from "@/features/application/api/applicationModels";
import {populateChartData, ChartConfigWithMapping} from "./chartDataPopulator";
import DEFAULT_HIGHCHART_CONFIG from "./defaultHighchartConfig";

type ChartValue = string | number | boolean | null | undefined;
type CategoryValue = string | number;
type PointCoordinate = Date | number | string | undefined;

interface BubblePoint extends Highcharts.Point {
    z?: number;
}

interface EnhancedChartComponentProps {
    highchartConfig: string;
    data: IDataTableData[];
    columnsMetadata: IColumnMetadata[];
}

/**
 * Validates the chart configuration before rendering.
 * @returns null if validation passes, or an error message if validation fails
 */
const validateChartConfig = (
    config: ChartConfigWithMapping,
    data: IDataTableData[]
): string | null => {
    // Check if seriesMapping exists
    if (!config.seriesMapping || config.seriesMapping.length === 0) {
        return "No series mapping found in chart configuration";
    }

    // Check if data exists
    if (!data || data.length === 0) {
        return "No data available for chart";
    }

    // Validate category column if specified
    const categoryColumn = config.seriesMapping.find(
        (m) => m.categoryColumn
    )?.categoryColumn;

    if (categoryColumn && !(categoryColumn in data[0])) {
        return `Category column "${categoryColumn}" not found in data`;
    }

    // Validate x and y columns exist in data
    for (const mapping of config.seriesMapping) {
        if (mapping.xColumn && !(mapping.xColumn in data[0])) {
            return `Column "${mapping.xColumn}" not found in data`;
        }
        if (mapping.yColumn && !(mapping.yColumn in data[0])) {
            return `Column "${mapping.yColumn}" not found in data`;
        }
        if (mapping.zColumn && !(mapping.zColumn in data[0])) {
            return `Column "${mapping.zColumn}" not found in data`;
        }
    }

    return null;
};

// Metadata and Mapping Utilities
const createMetadataLookup = (
    columnsMetadata: IColumnMetadata[]
): Map<string, IColumnMetadata> => {
    const lookup = new Map<string, IColumnMetadata>();
    columnsMetadata?.forEach((col) => {
        lookup.set(col.label, col);
        lookup.set(col.key, col);
    });
    return lookup;
};

const createSeriesMappingLookups = (
    mappings: ChartConfigWithMapping["seriesMapping"]
) => {
    const yColumnMap = new Map<string, string>();
    const zColumnMap = new Map<string, string>();
    const xColumnMap = new Map<string, string>();
    const categoryColumnMap = new Map<string, string>();
    const businessUnitColumnMap = new Map<string, string>();

    mappings?.forEach((mapping) => {
        yColumnMap.set(mapping.name, mapping.yColumn!);
        if (mapping.zColumn) zColumnMap.set(mapping.name, mapping.zColumn);
        if (mapping.xColumn) xColumnMap.set(mapping.name, mapping.xColumn);
        if (mapping.categoryColumn)
            categoryColumnMap.set(mapping.name, mapping.categoryColumn);
        if (mapping.businessUnitColumn)
            businessUnitColumnMap.set(mapping.name, mapping.businessUnitColumn);
    });

    return {
        yColumnMap,
        zColumnMap,
        xColumnMap,
        categoryColumnMap,
        businessUnitColumnMap
    };
};

// Value Formatting
const formatValue = (
    value: ChartValue,
    metadata: IColumnMetadata | undefined
): string => {
    if (metadata) {
        return dataUtils.dataTableDisplayValue(value, metadata);
    }
    return typeof value === "number" ? value.toFixed(2) : String(value);
};

const formatXValue = (
    xValue: PointCoordinate,
    categoryColumn: string | undefined,
    categories: CategoryValue[] | undefined
): string => {
    if (categoryColumn && typeof xValue === "number" && categories) {
        return categories[xValue] ? String(categories[xValue]) : String(xValue);
    } else if (typeof xValue === "number") {
        return Highcharts.dateFormat("%b %e, %Y", xValue);
    }
    return String(xValue);
};

const getCategories = (
    xAxis: Highcharts.XAxisOptions | Highcharts.XAxisOptions[] | undefined
): CategoryValue[] | undefined => {
    if (Array.isArray(xAxis)) {
        return xAxis[0]?.categories as CategoryValue[] | undefined;
    }
    return xAxis?.categories as CategoryValue[] | undefined;
};

// Data Lookup
const findBusinessUnitValue = (
    data: IDataTableData[],
    businessUnitColumn: string,
    xSeriesKey: string,
    ySeriesKey: string,
    pointX: PointCoordinate,
    pointY: PointCoordinate
): string | null => {
    const epsilon = 0.0001;
    const thisX = typeof pointX === "number" ? pointX : parseFloat(String(pointX));
    const thisY = typeof pointY === "number" ? pointY : parseFloat(String(pointY));

    const matchingRow = data.find((row) => {
        const rowX =
            typeof row[xSeriesKey] === "number"
                ? row[xSeriesKey]
                : parseFloat(String(row[xSeriesKey]));
        const rowY =
            typeof row[ySeriesKey] === "number"
                ? row[ySeriesKey]
                : parseFloat(String(row[ySeriesKey]));

        return Math.abs(rowX - thisX) < epsilon && Math.abs(rowY - thisY) < epsilon;
    });

    return matchingRow?.[businessUnitColumn]
        ? String(matchingRow[businessUnitColumn])
        : null;
};

const resolveBusinessUnitColumn = (
    mappedColumn: string | undefined,
    data: IDataTableData[]
): string | undefined => {
    if (mappedColumn) return mappedColumn;
    return data.length > 0 && "BusinessUnit" in data[0] ? "BusinessUnit" : undefined;
};

// Chart-Specific Tooltip Formatters
const formatPieTooltip = (
    context: Highcharts.Point,
    metadataMap: Map<string, IColumnMetadata>,
    yColumnMap: Map<string, string>
): string => {
    const pointName = context.name || context.series.name;
    const yColumn = yColumnMap.get(pointName);
    const metadata = yColumn ? metadataMap.get(yColumn) : undefined;
    const formattedValue = formatValue(context.y, metadata);
    const label = metadata?.label || yColumn || "Value";

    return `<b>${label}</b>: ${formattedValue}`;
};

const formatSharedTooltip = (
    points: Highcharts.Point[],
    xValue: PointCoordinate,
    metadataMap: Map<string, IColumnMetadata>,
    categoryColumnMap: Map<string, string>,
    seriesMapping: ChartConfigWithMapping["seriesMapping"],
    categories: CategoryValue[] | undefined,
    chartType: string | undefined,
    xAxisType: string | undefined
): string => {
    const firstSeriesName = points[0]?.series.name;
    const categoryColumn = firstSeriesName
        ? categoryColumnMap.get(firstSeriesName)
        : undefined;
    const categoryMeta = categoryColumn ? metadataMap.get(categoryColumn) : null;

    const formattedX = formatXValue(xValue, categoryColumn, categories);
    const xLabel = categoryMeta?.label || categoryColumn || "";

    const showXHeader = xAxisType !== "category" && chartType !== "scatter";
    let tooltip = showXHeader
        ? `<b>${formattedX}</b><br/>`
        : categoryColumn
        ? `<b>${xLabel}</b>: ${formattedX}<br/>`
        : "";

    points.forEach((point) => {
        const seriesKey =
            seriesMapping?.find((m) => m.name === point.series.name)?.yColumn || "";
        const metadata = metadataMap.get(seriesKey);
        const formattedValue = formatValue(point.y, metadata);
        const label = metadata?.label || seriesKey || point.series.name;

        tooltip += `<span style="color:${point.color}">●</span> <b>${label}</b>: ${formattedValue}<br/>`;
    });

    return tooltip;
};

const formatBubbleTooltip = (
    context: Highcharts.Point,
    data: IDataTableData[],
    metadataMap: Map<string, IColumnMetadata>,
    seriesLookups: ReturnType<typeof createSeriesMappingLookups>
): string => {
    const {yColumnMap, zColumnMap, xColumnMap, businessUnitColumnMap} =
        seriesLookups;
    const seriesName = context.series.name;

    const ySeriesKey = yColumnMap.get(seriesName) || "";
    const xSeriesKey = xColumnMap.get(seriesName) || "";
    const zSeriesKey = zColumnMap.get(seriesName);

    const yMeta = metadataMap.get(ySeriesKey);
    const xMeta = metadataMap.get(xSeriesKey);
    const zMeta = zSeriesKey ? metadataMap.get(zSeriesKey) : undefined;

    const formattedY = formatValue(context.y, yMeta);
    const formattedX = formatValue(context.x, xMeta);
    const zValue = (context as BubblePoint).z;
    const formattedZ = formatValue(zValue, zMeta);

    const xLabel = xMeta?.label || xSeriesKey || "X";
    const yLabel = yMeta?.label || ySeriesKey || "Y";
    const zLabel = zMeta?.label || zSeriesKey || "Size";

    const businessUnitColumn = resolveBusinessUnitColumn(
        businessUnitColumnMap.get(seriesName),
        data
    );
    const businessUnit =
        businessUnitColumn && xSeriesKey && ySeriesKey
            ? findBusinessUnitValue(
                  data,
                  businessUnitColumn,
                  xSeriesKey,
                  ySeriesKey,
                  context.x,
                  context.y
              )
            : null;

    const baseTooltip = `<b>${xLabel}</b>: ${formattedX}<br/><b>${yLabel}</b>: ${formattedY}<br/><b>${zLabel}</b>: ${formattedZ}`;
    return businessUnit ? `<b>${businessUnit}</b><br/>${baseTooltip}` : baseTooltip;
};

const formatScatterTooltip = (
    context: Highcharts.Point,
    data: IDataTableData[],
    metadataMap: Map<string, IColumnMetadata>,
    seriesLookups: ReturnType<typeof createSeriesMappingLookups>
): string => {
    const {yColumnMap, xColumnMap, businessUnitColumnMap} = seriesLookups;
    const seriesName = context.series.name;

    const ySeriesKey = yColumnMap.get(seriesName) || "";
    const xSeriesKey = xColumnMap.get(seriesName) || "";

    const yMeta = metadataMap.get(ySeriesKey);
    const xMeta = metadataMap.get(xSeriesKey);

    const formattedY = formatValue(context.y, yMeta);
    const formattedX = formatValue(context.x, xMeta);

    const xLabel = xMeta?.label || xSeriesKey || "X";
    const yLabel = yMeta?.label || ySeriesKey || seriesName;

    const businessUnitColumn = resolveBusinessUnitColumn(
        businessUnitColumnMap.get(seriesName),
        data
    );
    const businessUnit =
        businessUnitColumn && xSeriesKey && ySeriesKey
            ? findBusinessUnitValue(
                  data,
                  businessUnitColumn,
                  xSeriesKey,
                  ySeriesKey,
                  context.x,
                  context.y
              )
            : null;

    return businessUnit
        ? `<b>${businessUnit}</b><br/><b>${yLabel}</b>: ${formattedY}<br/><b>${xLabel}</b>: ${formattedX}`
        : `<b>${yLabel}</b>: ${formattedY}<br/><b>${xLabel}</b>: ${formattedX}`;
};

const formatDefaultTooltip = (
    context: Highcharts.Point,
    metadataMap: Map<string, IColumnMetadata>,
    seriesLookups: ReturnType<typeof createSeriesMappingLookups>,
    categories: CategoryValue[] | undefined
): string => {
    const {yColumnMap, categoryColumnMap} = seriesLookups;
    const seriesName = context.series.name;

    const ySeriesKey = yColumnMap.get(seriesName) || "";
    const categoryColumn = categoryColumnMap.get(seriesName);

    const yMeta = metadataMap.get(ySeriesKey);
    const categoryMeta = categoryColumn
        ? metadataMap.get(categoryColumn)
        : undefined;

    const formattedY = formatValue(context.y, yMeta);
    const formattedX = formatXValue(context.x, categoryColumn, categories);

    const xLabel = categoryMeta?.label || categoryColumn;
    return xLabel
        ? `<b>${xLabel}</b>: ${formattedX}<br/><b>${seriesName}</b>: ${formattedY}`
        : `<b>${seriesName}</b><br/><b>${formattedX}</b>: ${formattedY}`;
};

// Main Tooltip Formatter Factory
const createTooltipFormatter = (
    baseConfig: ChartConfigWithMapping,
    populatedConfig: ChartConfigWithMapping,
    data: IDataTableData[],
    metadataMap: Map<string, IColumnMetadata>,
    seriesLookups: ReturnType<typeof createSeriesMappingLookups>
) => {
    return function (this: Highcharts.Point): string {
        const chartType = baseConfig.chart?.type;
        const xAxisType = Array.isArray(baseConfig.xAxis)
            ? baseConfig.xAxis[0]?.type
            : baseConfig.xAxis?.type;
        const categories = getCategories(
            populatedConfig.xAxis as
                | Highcharts.XAxisOptions
                | Highcharts.XAxisOptions[]
        );

        if (this.points) {
            return formatSharedTooltip(
                this.points,
                this.x,
                metadataMap,
                seriesLookups.categoryColumnMap,
                populatedConfig.seriesMapping,
                categories,
                chartType,
                xAxisType
            );
        } else if (chartType === "pie") {
            return formatPieTooltip(this, metadataMap, seriesLookups.yColumnMap);
        } else if (chartType === "bubble") {
            return formatBubbleTooltip(this, data, metadataMap, seriesLookups);
        } else if (chartType === "scatter") {
            return formatScatterTooltip(this, data, metadataMap, seriesLookups);
        } else {
            return formatDefaultTooltip(
                this,
                metadataMap,
                seriesLookups,
                categories
            );
        }
    };
};
// Error Boundary to prevent chart errors from crashing the entire page
interface ChartErrorBoundaryProps {
    children: ReactNode;
}

interface ChartErrorBoundaryState {
    hasError: boolean;
}

class ChartErrorBoundary extends Component<
    ChartErrorBoundaryProps,
    ChartErrorBoundaryState
> {
    constructor(props: ChartErrorBoundaryProps) {
        super(props);
        this.state = {hasError: false};
    }

    static getDerivedStateFromError(_: Error): ChartErrorBoundaryState {
        return {hasError: true};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        console.error("Chart rendering error:", error, errorInfo);
    }

    render() {
        if (this.state.hasError) {
            return null;
        }

        return this.props.children;
    }
}

/**
 * EnhancedChartComponent - A dynamic chart renderer that extends Highcharts configuration
 * with intelligent data mapping capabilities.
 *
 * This component takes a standard Highcharts configuration object and enhances it with a custom
 * `seriesMapping` property that allows dynamic binding of chart series to data table columns.
 * It automatically handles tooltip formatting, value formatting based on column metadata, and
 * supports various chart types (line, bar, pie, scatter, bubble, etc.).
 *
 * @example
 * ```tsx
 * const chartConfig = {
 *   chart: { type: 'line' },
 *   title: { text: 'Sales Over Time' },
 *   seriesMapping: [
 *     {
 *       name: 'Revenue',
 *       yColumn: 'TotalRevenue',
 *       categoryColumn: 'Date'
 *     },
 *     {
 *       name: 'Cost',
 *       yColumn: 'TotalCost',
 *       categoryColumn: 'Date'
 *     }
 *   ]
 * };
 *
 * <EnhancedChartComponent
 *   highchartConfig={JSON.stringify(chartConfig)}
 *   data={dataTableRows}
 *   columnsMetadata={columnDefinitions}
 * />
 * ```
 *
 * @property {string} highchartConfig - JSON string of Highcharts config with seriesMapping extension
 * @property {IDataTableData[]} data - Array of data table rows to visualize
 * @property {IColumnMetadata[]} columnsMetadata - Column definitions for formatting and labels
 *
 * **seriesMapping Property:**
 * - `name`: Display name for the series
 * - `yColumn`: Column key for Y-axis values (required)
 * - `xColumn`: Column key for X-axis values (for scatter/bubble charts)
 * - `zColumn`: Column key for Z-axis/size values (for bubble charts)
 * - `categoryColumn`: Column key for categorical X-axis values
 *
 * The component automatically:
 * - Validates configuration and data compatibility
 * - Populates chart series from data table
 * - Formats values using column metadata (currency, percentage, etc.)
 * - Generates intelligent tooltips based on chart type
 * - Handles category axes, datetime axes, and numeric axes
 */
const EnhancedChartComponent: React.FC<EnhancedChartComponentProps> = ({
    highchartConfig,
    data,
    columnsMetadata
}) => {
    const [error, setError] = useState<string | null>(null);
    const [baseConfig, setBaseConfig] = useState<ChartConfigWithMapping | null>(
        null
    );

    useEffect(() => {
        try {
            setError(null);
            const parsed: ChartConfigWithMapping = JSON.parse(highchartConfig);
            const mergedConfig = merge({}, DEFAULT_HIGHCHART_CONFIG, parsed);

            const validationError = validateChartConfig(mergedConfig, data);
            if (validationError) {
                setError(validationError);
                setBaseConfig(null);
                return;
            }

            setBaseConfig(mergedConfig);
        } catch (err) {
            setError("Invalid chart configuration");
            setBaseConfig(null);
        }
    }, [highchartConfig, data]);

    const metadataMap = useMemo(
        () => createMetadataLookup(columnsMetadata),
        [columnsMetadata]
    );

    const populatedConfig = useMemo(() => {
        if (error || !baseConfig) {
            return null;
        }

        try {
            const result = populateChartData(baseConfig, data);
            if (!result) {
                return null;
            }
            return result;
        } catch (err) {
            return null;
        }
    }, [baseConfig, data, error]);

    const seriesLookups = useMemo(() => {
        if (!populatedConfig?.seriesMapping && !baseConfig?.seriesMapping) {
            return null;
        }
        return createSeriesMappingLookups(
            populatedConfig?.seriesMapping || baseConfig?.seriesMapping || []
        );
    }, [populatedConfig?.seriesMapping, baseConfig?.seriesMapping]);

    const tooltipFormatter = useMemo(() => {
        if (!baseConfig || !populatedConfig || !seriesLookups) {
            return undefined;
        }
        return createTooltipFormatter(
            baseConfig,
            populatedConfig,
            data,
            metadataMap,
            seriesLookups
        );
    }, [baseConfig, populatedConfig, data, metadataMap, seriesLookups]);

    const enhancedConfig = useMemo(() => {
        if (!populatedConfig || !tooltipFormatter) {
            return null;
        }

        return {
            ...populatedConfig,
            tooltip: {
                ...populatedConfig.tooltip,
                useHTML: true,
                shared: true,
                formatter: tooltipFormatter
            }
        } as Highcharts.Options;
    }, [populatedConfig, tooltipFormatter]);

    if (error || !baseConfig || !populatedConfig || !enhancedConfig) {
        return null;
    }

    try {
        return (
            <ChartErrorBoundary>
                <Chart
                    //These are typed as any because there is a mismatch between the highcharts types in the react package and the official package but we need the official package to get the export

                    highcharts={Highcharts as any}
                    options={enhancedConfig as any}
                    containerProps={{
                        style: {
                            width: "100%",
                            height: "400px"
                        }
                    }}
                />
            </ChartErrorBoundary>
        );
    } catch (err) {
        console.error("Failed to render chart:", err);
        return null;
    }
};

export default memo(EnhancedChartComponent);
